import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

interface Subscription {
  _id: string;
  name: string;
  price: string;
  duration: string;
  users: string;
  leadAccess: string;
  dailyUnlock: string;
  transactionId: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  startDate?: string;
  expiryDate?: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    username: string;
  };
}

export default function AdminLeadSubscriptions() {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'expired'>('all');
  const [search, setSearch] = useState('');

  const fetchSubscriptions = async () => {
    try {
      if (!token) return;
      setLoading(true);
      const data = await api.adminGetSubscriptions(token);
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [token]);

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
    if (!token) return;
    try {
      await api.adminUpdateSubscriptionStatus(token, id, status);
      toast.success(`Subscription successfully ${status}!`);
      fetchSubscriptions();
    } catch (err) {
      toast.error(`Failed to update status`);
    }
  };

  const calculateRemainingDays = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesFilter = filter === 'all' || sub.status === filter;
    const searchLower = search.toLowerCase();
    const matchesSearch =
      sub.userId?.name?.toLowerCase().includes(searchLower) ||
      sub.userId?.email?.toLowerCase().includes(searchLower) ||
      sub.name?.toLowerCase().includes(searchLower) ||
      sub.transactionId?.toLowerCase().includes(searchLower);

    return matchesFilter && matchesSearch;
  });

  const getCounts = () => {
    return {
      all: subscriptions.length,
      pending: subscriptions.filter(s => s.status === 'pending').length,
      approved: subscriptions.filter(s => s.status === 'approved').length,
      rejected: subscriptions.filter(s => s.status === 'rejected').length,
      expired: subscriptions.filter(s => s.status === 'expired').length,
    };
  };

  const counts = getCounts();

  return (
    <AdminLayout>
      <div className="p-3 min-h-screen font-sans bg-slate-50">
        <div className="max-w-7xl mx-auto space-y-3 px-2">
          {/* Top Banner/Header */}
          <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-none">Lead Plan Subscriptions</h1>
              <p className="text-sm text-slate-500 font-medium mt-2">Manage and approve lead plans purchased by brokers.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchSubscriptions}
                className="w-10 h-10 rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all flex items-center justify-center shadow-sm active:scale-95"
                title="Refresh Subscriptions"
              >
                <i className="fa-solid fa-arrows-rotate"></i>
              </button>
            </div>
          </div>

          {/* Counts & Filter Tabs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { type: 'all', label: 'All', count: counts.all, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100', icon: 'fa-layer-group' },
              { type: 'pending', label: 'Pending', count: counts.pending, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: 'fa-clock' },
              { type: 'approved', label: 'Approved', count: counts.approved, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'fa-check-circle' },
              { type: 'rejected', label: 'Rejected', count: counts.rejected, color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', icon: 'fa-times-circle' },
              { type: 'expired', label: 'Expired', count: counts.expired, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', icon: 'fa-ban' },
            ].map(tab => (
              <button
                key={tab.type}
                onClick={() => setFilter(tab.type as any)}
                className={`p-4 rounded-xl border transition-all duration-200 text-left flex items-center justify-between group ${
                  filter === tab.type
                    ? 'bg-white border-blue-500 ring-1 ring-blue-500 shadow-sm'
                    : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
                }`}
              >
                <div>
                  <span className={`text-xs font-bold tracking-wide uppercase ${tab.color}`}>{tab.label}</span>
                  <h3 className="text-xl font-bold text-slate-900 mt-1">{tab.count}</h3>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${tab.bg} ${tab.color} border ${tab.border}`}>
                  <i className={`fa-solid ${tab.icon}`}></i>
                </div>
              </button>
            ))}
          </div>

          {/* Search & Main Table Area */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-200 bg-white flex flex-col md:flex-row gap-4 justify-between items-center">
              <div className="relative flex-1 max-w-md w-full">
                <i className="fa-solid fa-search absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search broker, email, plan or Txn ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm text-slate-900 transition-all"
                />
              </div>
              <div className="text-sm font-medium text-slate-500">
                Matches: <span className="text-slate-900 font-bold">{filteredSubscriptions.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-16 flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm font-medium text-slate-500">Loading subscriptions...</p>
                </div>
              ) : filteredSubscriptions.length === 0 ? (
                <div className="p-16 text-center flex flex-col items-center justify-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 text-2xl mb-4">
                    <i className="fa-solid fa-inbox"></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">No Subscriptions Found</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm">
                    No records match your current filters and search query.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Broker</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Plan Details</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Dates</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide text-center">Days Left</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wide text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredSubscriptions.map(sub => (
                      <tr key={sub._id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                                  {(sub.userId?.name || 'B').charAt(0).toUpperCase()}
                                </div>
                                <div>
                              <p className="text-sm font-bold text-slate-900">
                                {sub.userId?.name || 'Unknown User'}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">{sub.userId?.email || 'N/A'}</p>
                            </div>
                            </div>
                        </td>
                        <td className="px-4 py-2">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{sub.name}</p>
                            <div className="flex gap-1 items-baseline mt-1">
                              <span className="text-xs font-bold text-slate-700">₹{sub.price.replace(/[^0-9]/g, '')}</span>
                              <span className="text-xs text-slate-500">/ {sub.duration}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">Txn: <span className="font-mono text-slate-700">{sub.transactionId.slice(-8).toUpperCase()}</span></p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-0.5 text-xs">
                              <div className="flex gap-1">
                                  <span className="font-semibold text-slate-500 w-12">Start:</span>
                                  <span className="text-slate-900">
                                      {sub.startDate ? new Date(sub.startDate).toLocaleDateString() : 'N/A'}
                                  </span>
                            </div>
                            <div className="flex gap-1">
                                  <span className="font-semibold text-slate-500 w-12">End:</span>
                                  <span className="text-slate-900">
                                      {sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : 'N/A'}
                                  </span>
                              </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {sub.status === 'approved' ? (
                            <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold ${
                              (calculateRemainingDays(sub.expiryDate) || 0) <= 7 
                                ? 'bg-rose-100 text-rose-700' 
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {calculateRemainingDays(sub.expiryDate) ?? 'N/A'} days
                            </span>
                          ) : sub.status === 'expired' ? (
                            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-500">
                              0 days
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {sub.status === 'approved' ? (
                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Approved
                            </span>
                          ) : sub.status === 'rejected' ? (
                            <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span> Rejected
                            </span>
                          ) : sub.status === 'expired' ? (
                            <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span> Expired
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-md font-bold text-xs inline-flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            {(sub.status === 'pending' || sub.status === 'expired' || sub.status === 'rejected') && (
                              <button
                                onClick={() => handleUpdateStatus(sub._id, 'approved')}
                                className="px-3 py-1.5 rounded-md bg-white border border-emerald-200 hover:bg-emerald-50 text-emerald-600 font-bold text-xs transition-colors shadow-sm"
                              >
                                Approve
                              </button>
                            )}
                            {sub.status !== 'rejected' && sub.status !== 'expired' && (
                              <button
                                onClick={() => handleUpdateStatus(sub._id, 'rejected')}
                                className="px-3 py-1.5 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 font-bold text-xs transition-colors shadow-sm"
                              >
                                Reject
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
